import { useMemo } from 'react';
import { components } from 'react-select';
import { Select } from '~/components';
import { isSingleValue } from '~/components/select';
import { useDirective, useFormState } from '~/hooks';
import { isSelectDirective } from '~/types';
import { UserIP } from './user-ip';

import { type UseFormRegister } from 'react-hook-form';
import type { GroupBase, OptionProps } from 'react-select';
import type { SelectOnChange } from '~/components/select';
import type { Directive, FormData, OnChangeArgs, SingleOption } from '~/types';

type OptionWithDescription = SingleOption<{ description: string | null }>;

interface QueryTargetProps {
  name: string;
  placeholder: string;
  onChange(e: OnChangeArgs): void;
  register: UseFormRegister<FormData>;
}

function buildOptions(directive: Nullable<Directive>): OptionWithDescription[] {
  if (directive !== null && isSelectDirective(directive)) {
    return directive.options.map(o => ({
      value: o.value,
      label: o.name,
      data: { description: o.description },
    }));
  }
  return [];
}

const Option = (props: OptionProps<OptionWithDescription, false>) => {
  const { label, data } = props;
  return (
    <components.Option<OptionWithDescription, false, GroupBase<OptionWithDescription>> {...props}>
      <span className="text-slate-800 dark:text-on-surface font-medium">{label}</span>
      <br />
      <span className="text-xs text-slate-500 dark:text-on-surface-variant">
        {data.data?.description}
      </span>
    </components.Option>
  );
};

export const QueryTarget = (props: QueryTargetProps): JSX.Element => {
  const { name, register, onChange, placeholder } = props;

  const displayTarget = useFormState(s => s.target.display);
  const setTarget = useFormState(s => s.setTarget);
  const queryTarget = useFormState(s => s.form.queryTarget);
  const directive = useDirective();

  const options = useMemo(() => buildOptions(directive), [directive]);
  const isSelect = useMemo(() => directive !== null && isSelectDirective(directive), [directive]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setTarget({ display: e.target.value });
    onChange({ field: name, value: [e.target.value] });
  }

  const handleSelectChange: SelectOnChange<OptionWithDescription> = e => {
    if (isSingleValue(e)) {
      onChange({ field: name, value: e.value });
      setTarget({ display: e.value });
    }
  };

  const handleUserIPChange = (target: string): void => {
    setTarget({ display: target });
    onChange({ field: name, value: target });
  };

  return (
    <>
      <input {...register('queryTarget')} hidden readOnly value={queryTarget} />
      {isSelect ? (
        <Select<OptionWithDescription, false>
          name={name}
          options={options}
          components={{ Option }}
          onChange={handleSelectChange}
        />
      ) : (
        <div className="bg-white dark:bg-surface-container-lowest border border-slate-200 dark:border-outline-variant rounded-lg flex items-center w-full relative focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent h-14 shadow-sm">
          <span className="material-symbols-outlined text-slate-400 dark:text-on-surface-variant absolute left-4 pointer-events-none">
            search
          </span>
          <input
            type="text"
            className="w-full bg-transparent border-0 outline-none text-slate-800 dark:text-on-surface placeholder-slate-400 dark:placeholder-on-surface-variant pl-12 pr-24 py-4 font-code-sm text-body-lg focus:ring-0 focus:outline-none focus:border-transparent"
            value={displayTarget}
            aria-label={placeholder}
            placeholder={placeholder}
            name="queryTargetDisplay"
            onChange={handleInputChange}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
            <UserIP setTarget={handleUserIPChange} />
          </div>
        </div>
      )}
    </>
  );
};
